import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import DocumentViewer from "../components/DocumentViewer";

interface Highlight {
  id: string;
  text: string;
  comment: string;
  tags: string[];
}

// Dummy metadata lookup – in a real app this would come from Supabase.
function useSubmissionMeta(submissionId: string | undefined) {
  // TODO: Replace with real data fetching. For now we return a hard-coded
  // document URL so the page has something to render.
  return useMemo(() => {
    // naive mapping – just alternate between a few sample docs placed under
    // the public `ref/document` folder for UX testing.
    const index = Number(submissionId) || 1;
    const samples = [
      { url: "/ref/document/example.docx", type: "docx" as const },
      { url: "/ref/document/example.pdf", type: "pdf" as const },
      { url: "/ref/document/example.md", type: "markdown" as const },
    ];
    return samples[(index - 1) % samples.length];
  }, [submissionId]);
}

export default function MarkingViewPage() {
  const { submissionId } = useParams();
  const submission = useSubmissionMeta(submissionId);

  // Small convenience helper to switch between the 3 sample documents whilst
  // we are still iterating on the UX.  Once real data is wired up this can be
  // removed.
  const quickSwitch = (
    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
      <span className="font-medium">Test sample:</span>
      {[1, 2, 3].map((i) => (
        <a
          key={i}
          href={`/app/marking/${i}`}
          className={
            [
              "rounded px-2 py-0.5 hover:underline",
              Number(submissionId || "1") === i
                ? "bg-primary/10 text-primary dark:bg-primary/30"
                : "bg-gray-200/60 dark:bg-gray-700/60",
            ].join(" ")
          }
        >
          {i === 1 ? "DOCX" : i === 2 ? "PDF" : "MD"}
        </a>
      ))}
    </div>
  );

  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const docContainerRef = useRef<HTMLDivElement>(null);

  const addHighlight = useCallback((text: string, comment: string) => {
    setHighlights((prev) => [
      ...prev,
      { id: crypto.randomUUID(), text, comment, tags: [] },
    ]);
  }, []);

  // Attach a mouse-up handler that looks at the current text selection and, if
  // any, asks the educator to leave a comment. The selected range is wrapped
  // in <mark data-comment="…"> so we can later display tooltips.
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;

    const selectedText = sel.toString();
    if (selectedText.trim().length === 0) return;

    const comment = window.prompt("Comment for this highlight?");
    if (comment === null) {
      sel.removeAllRanges();
      return; // user cancelled
    }

    try {
      const range = sel.getRangeAt(0);

      // Ensure the selection is inside *our* document container.
      if (!docContainerRef.current?.contains(range.startContainer)) {
        sel.removeAllRanges();
        return;
      }

      const highlightEl = document.createElement("mark");
      highlightEl.className =
        "bg-yellow-200 dark:bg-yellow-800/40 rounded-sm cursor-pointer";
      highlightEl.setAttribute("data-comment", comment);
      highlightEl.title = comment;

      // Extract & re-insert to safely wrap multi-node selections.
      const frag = range.extractContents();
      highlightEl.appendChild(frag);
      range.insertNode(highlightEl);
    } catch (err) {
      // In case the range cannot be wrapped (e.g. due to it spanning multiple
      // elements) we just ignore for now. A robust solution would use a dedicated
      // annotation library, but that is outside the scope of this initial POC.
      console.warn("Failed to apply highlight", err);
    }

    addHighlight(selectedText, comment);
    sel.removeAllRanges();
  }, [addHighlight]);

  // Handle tag drops onto existing highlights.
  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Allow dropping.
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const tag = e.dataTransfer.getData("text/plain");
      if (!tag) return;

      let el = e.target as HTMLElement | null;
      while (el && el !== docContainerRef.current && el.tagName !== "MARK") {
        el = el.parentElement;
      }
      if (!el || el.tagName !== "MARK") return;

      const comment = el.getAttribute("data-comment") || "";

      // Update DOM dataset.
      const existingTags: string[] = JSON.parse(el.dataset.tags || "[]");
      if (!existingTags.includes(tag)) {
        existingTags.push(tag);
        el.dataset.tags = JSON.stringify(existingTags);
      }

      // Update React state.
      setHighlights((prev) =>
        prev.map((h) =>
          h.comment === comment && h.text.includes(el?.textContent || "")
            ? { ...h, tags: existingTags }
            : h
        )
      );
    },
    []
  );

  return (
    <div className="relative flex h-full gap-4 overflow-hidden">
      {/* Quick sample switcher – development only */}
      <div className="absolute right-6 top-6 z-20">
        {quickSwitch}
      </div>
      {/* Document area */}
      <div
        ref={docContainerRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onMouseUp={handleMouseUp}
        className="flex-1 h-full overflow-auto rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
      >
        <DocumentViewer url={submission.url} type={submission.type} />
      </div>

      {/* Sidebar */}
      <aside className="w-80 flex-shrink-0 overflow-y-auto space-y-6 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
        {/* Tags */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">
            Quick-tags
          </h2>
          <div className="flex flex-wrap gap-2">
            {[
              "Spelling",
              "Grammar",
              "Structure",
              "Evidence",
              "Citations",
              "Clarity",
            ].map((tag) => (
              <span
                key={tag}
                className="select-none cursor-pointer rounded-md bg-primary/10 text-primary dark:bg-primary/20 px-2 py-1 text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary/30"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", tag);
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </section>

        {/* Highlights list */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">
            Highlights
          </h2>
          <ul className="space-y-2 text-sm">
            {highlights.length === 0 && (
              <li className="text-gray-500">No highlights yet.</li>
            )}
            {highlights.map((h) => (
              <li key={h.id} className="rounded-md border p-2 space-y-1">
                <p className="font-medium text-gray-800 dark:text-gray-200">
                  {h.text.length > 80 ? h.text.slice(0, 77) + "…" : h.text}
                </p>
                <p className="mt-1 text-gray-600 dark:text-gray-400 text-xs">
                  {h.comment}
                </p>
                {h.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {h.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded bg-primary/10 dark:bg-primary/20 text-primary px-1.5 py-0.5 text-[10px] font-medium"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* General comments */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 mb-2">
            General comments
          </h2>
          <textarea
            placeholder="Overall feedback for this submission…"
            className="w-full min-h-[120px] rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
        </section>
      </aside>
    </div>
  );
}
